import styled, { keyframes, css } from "styled-components";
// Path assumes this file lives at
// src/components/sections/Availability/Availability.styles.js
// — a sibling of the Works/ section folder, so the import depth to
// common/colors matches Works.styles.js exactly.
import { COLORS, INDIGO_CSS_VARS } from "../../common/colors";

// ─── Keyframes ─────────────────────────────────────────────────────────────────

export const kFadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0);    }
`;

export const kFadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const motion = (animCss) => css`
  @media (prefers-reduced-motion: no-preference) { ${animCss} }
`;

// Desktop breakpoint used throughout this file — below it the page behaves
// as a mobile step-accordion, at/above it every section is shown expanded
// in a 3-column layout.
const DESKTOP = "64em";

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION ROOT
// ═══════════════════════════════════════════════════════════════════════════════

export const Section = styled.section`
  ${INDIGO_CSS_VARS}

  --r-sm: 10px;
  --r:    14px;
  --r-lg: 18px;
  --r-full: 999px;

  --s-sm: 0 2px 10px rgba(0,0,0,0.05);
  --s:    0 4px 20px rgba(0,0,0,0.08);

  --sp-2: 0.5rem;
  --sp-3: 0.75rem;
  --sp-4: 1rem;
  --sp-5: 1.25rem;
  --sp-6: 1.5rem;
  --sp-8: 2rem;

  --t-fast: 0.15s ease;
  --t:      0.22s ease;

  position: relative;
  width: 100%;
  min-height: 100%;
  background-color: ${COLORS.pageBg};
  padding-bottom: var(--sp-8);
`;
Section.displayName = "Section";

export const Inner = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 1180px;
  margin: 0 auto;
  padding: var(--sp-5) var(--sp-4) 0;

  @media (min-width: 48em) { padding: var(--sp-6) var(--sp-5) 0; }
  @media (min-width: ${DESKTOP}) { padding: var(--sp-8) var(--sp-6) 0; }
`;
Inner.displayName = "Inner";

// ═══════════════════════════════════════════════════════════════════════════════
// HEADER
// ═══════════════════════════════════════════════════════════════════════════════

export const HeaderWrap = styled.div`
  text-align: center;
  margin-bottom: var(--sp-6);
  ${motion(css`animation: ${kFadeUp} 0.5s ease both;`)}
`;
HeaderWrap.displayName = "HeaderWrap";

export const TopIconWrap = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: var(--c-tint);
  color: var(--c-accent);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--sp-3);

  svg { width: 24px; height: 24px; }
`;
TopIconWrap.displayName = "TopIconWrap";

export const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 800;
  color: var(--c-accent);
  margin: 0 0 var(--sp-2);
  letter-spacing: -0.3px;

  @media (min-width: ${DESKTOP}) { font-size: 2.1rem; }
`;
Title.displayName = "Title";

export const Subtitle = styled.p`
  font-size: 0.9rem;
  color: var(--c-text-muted, #6b6b63);
  max-width: 420px;
  margin: 0 auto;
  line-height: 1.5;

  @media (min-width: ${DESKTOP}) { font-size: 1rem; max-width: none; }
`;
Subtitle.displayName = "Subtitle";

// ─── Trust markers ─────────────────────────────────────────────────────────────

export const TrustRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: var(--sp-2);
  background: var(--c-surface);
  border: 1px solid var(--c-border);
  border-radius: var(--r-sm);
  padding: var(--sp-3);
  margin: var(--sp-5) 0 var(--sp-6);
  box-shadow: var(--s-sm);

  @media (min-width: ${DESKTOP}) {
    background: none;
    border: none;
    box-shadow: none;
    justify-content: center;
    align-items: center;
    gap: var(--sp-4);
    padding: 0;
  }
`;
TrustRow.displayName = "TrustRow";

export const TrustItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;

  svg { flex-shrink: 0; width: 18px; height: 18px; color: var(--c-accent); }

  span {
    font-size: 0.72rem;
    line-height: 1.25;
    color: var(--c-text-muted, #6b6b63);
    font-weight: 600;
  }

  @media (min-width: ${DESKTOP}) {
    flex: 0 0 auto;
    span { font-size: 0.9rem; }
  }
`;
TrustItem.displayName = "TrustItem";

export const TrustDot = styled.span`
  display: none;
  color: var(--c-border-accent, var(--c-border));

  @media (min-width: ${DESKTOP}) { display: inline-block; }
`;
TrustDot.displayName = "TrustDot";

// ═══════════════════════════════════════════════════════════════════════════════
// GRID LAYOUT
// ═══════════════════════════════════════════════════════════════════════════════

export const GridWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);

  @media (min-width: ${DESKTOP}) {
    display: grid;
    grid-template-columns: 1fr 1fr 340px;
    grid-template-areas:
      "date    time    aside"
      "details details details";
    /* stretch (not start) so the Date, Time, and Selection cards in row 1
       all match the height of the tallest one instead of trailing off
       unevenly against each other. */
    align-items: stretch;
    gap: var(--sp-5);
  }
`;
GridWrap.displayName = "GridWrap";

const gridArea = (name) => css`
  @media (min-width: ${DESKTOP}) { grid-area: ${name}; }
`;

// ═══════════════════════════════════════════════════════════════════════════════
// ACCORDION CARD (Date / Time / Details)
// ═══════════════════════════════════════════════════════════════════════════════

export const Card = styled.div`
  background: var(--c-surface);
  border: 1px solid var(--c-border);
  border-radius: var(--r-lg);
  box-shadow: var(--s-sm);
  overflow: hidden;
  ${({ $area }) => $area && gridArea($area)}
`;
Card.displayName = "Card";

export const CardHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  padding: var(--sp-4) var(--sp-4);
  min-height: 44px;
  color: inherit;

  &:disabled { cursor: default; }
  &:focus-visible { outline: 2px solid var(--c-accent); outline-offset: -2px; }

  @media (min-width: ${DESKTOP}) { cursor: default; padding: var(--sp-5) var(--sp-5) var(--sp-2); }
`;
CardHeader.displayName = "CardHeader";

export const CardHeaderIcon = styled.span`
  flex-shrink: 0;
  color: var(--c-accent);
  display: inline-flex;
  svg { width: 18px; height: 18px; }
`;
CardHeaderIcon.displayName = "CardHeaderIcon";

export const CardHeaderTitle = styled.span`
  flex: 1;
  font-size: 0.95rem;
  font-weight: 700;
  color: #2e1f0f;

  ${({ $muted }) => $muted && css`color: var(--c-text-muted, #a3a396);`}
`;
CardHeaderTitle.displayName = "CardHeaderTitle";

export const CardHeaderRight = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;

  @media (min-width: ${DESKTOP}) { display: none; }
`;
CardHeaderRight.displayName = "CardHeaderRight";

export const Chevron = styled.svg.attrs({
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
})`
  width: 18px;
  height: 18px;
  color: ${({ $muted }) => ($muted ? "var(--c-text-muted, #c7c7bd)" : "var(--c-accent)")};
  transition: transform var(--t);
  transform: rotate(${({ $open }) => ($open ? "180deg" : "0deg")});
`;
Chevron.displayName = "Chevron";

export const EditBtn = styled.button`
  background: none;
  border: none;
  color: var(--c-accent);
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
  padding: 6px 4px;
  min-height: 32px;

  &:focus-visible { outline: 2px solid var(--c-accent); outline-offset: 1px; }
`;
EditBtn.displayName = "EditBtn";

// ─── Collapsed summary row (mobile-only "done" state) ──────────────────────────

export const SummaryRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-3);
  margin: 0 var(--sp-4) var(--sp-4);
  padding: 10px 14px;
  background: var(--c-tint);
  border-radius: var(--r-sm);

  @media (min-width: ${DESKTOP}) { display: none; }
`;
SummaryRow.displayName = "SummaryRow";

export const SummaryValue = styled.span`
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--c-accent);
`;
SummaryValue.displayName = "SummaryValue";

// ─── Body (collapses on mobile, always open on desktop) ────────────────────────

export const CardBody = styled.div`
  display: ${({ $open }) => ($open ? "block" : "none")};
  padding: 0 var(--sp-4) var(--sp-4);

  @media (min-width: ${DESKTOP}) {
    display: block;
    padding: 0 var(--sp-5) var(--sp-5);
  }
`;
CardBody.displayName = "CardBody";

// ═══════════════════════════════════════════════════════════════════════════════
// CALENDAR
// ═══════════════════════════════════════════════════════════════════════════════

export const CalendarNav = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--sp-4);
`;
CalendarNav.displayName = "CalendarNav";

export const CalendarNavBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid var(--c-border);
  background: var(--c-surface);
  color: var(--c-accent);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background var(--t-fast);

  svg { width: 14px; height: 14px; }

  &:hover:not(:disabled) { background: var(--c-tint); }
  &:disabled { opacity: 0.3; cursor: not-allowed; }
  &:focus-visible { outline: 2px solid var(--c-accent); outline-offset: 2px; }
`;
CalendarNavBtn.displayName = "CalendarNavBtn";

export const CalendarLabel = styled.strong`
  font-size: 0.92rem;
  font-weight: 700;
  color: #2e1f0f;
`;
CalendarLabel.displayName = "CalendarLabel";

export const WeekRow = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  margin-bottom: 6px;
`;
WeekRow.displayName = "WeekRow";

export const WeekdayLabel = styled.span`
  text-align: center;
  font-size: 0.68rem;
  font-weight: 700;
  color: var(--c-text-muted, #a3a396);
`;
WeekdayLabel.displayName = "WeekdayLabel";

export const DaysGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  row-gap: 4px;
`;
DaysGrid.displayName = "DaysGrid";

export const DayCell = styled.button`
  aspect-ratio: 1;
  border: none;
  background: ${({ $selected }) => ($selected ? "var(--c-accent)" : "transparent")};
  color: ${({ $selected, $inMonth, $disabled }) =>
    $selected ? "#fff" : !$inMonth || $disabled ? "var(--c-text-muted, #c7c7bd)" : "#2e1f0f"};
  border-radius: 50%;
  font-size: 0.82rem;
  font-weight: ${({ $selected }) => ($selected ? "700" : "500")};
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  width: 32px;
  transition: background var(--t-fast), color var(--t-fast);

  &:hover:not(:disabled) {
    background: ${({ $selected }) => ($selected ? "var(--c-accent)" : "var(--c-tint)")};
  }
  &:focus-visible { outline: 2px solid var(--c-accent); outline-offset: 2px; }
`;
DayCell.displayName = "DayCell";

export const CalendarLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-4);
  margin-top: var(--sp-4);
  padding-top: var(--sp-3);
  border-top: 1px solid var(--c-border);
`;
CalendarLegend.displayName = "CalendarLegend";

export const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.72rem;
  color: var(--c-text-muted, #6b6b63);

  &::before {
    content: "";
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ $dot }) => $dot || "transparent"};
    border: ${({ $dot }) => ($dot ? "none" : "1px solid var(--c-border)")};
  }
`;
LegendItem.displayName = "LegendItem";

// ═══════════════════════════════════════════════════════════════════════════════
// TIME SELECTION
// ═══════════════════════════════════════════════════════════════════════════════

export const DateChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: var(--c-tint);
  color: var(--c-accent);
  font-size: 0.8rem;
  font-weight: 700;
  padding: 8px 14px;
  border-radius: var(--r-sm);
  margin-bottom: var(--sp-4);

  svg { width: 14px; height: 14px; }
`;
DateChip.displayName = "DateChip";

export const TimeGroup = styled.div`
  margin-bottom: var(--sp-4);
  &:last-of-type { margin-bottom: 0; }
`;
TimeGroup.displayName = "TimeGroup";

export const TimeGroupLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--c-text-muted, #6b6b63);
  margin-bottom: var(--sp-2);

  svg { width: 14px; height: 14px; color: var(--c-accent); }
`;
TimeGroupLabel.displayName = "TimeGroupLabel";

export const TimeSlotsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
`;
TimeSlotsGrid.displayName = "TimeSlotsGrid";

export const TimeSlot = styled.button`
  padding: 10px 4px;
  border-radius: var(--r-sm);
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
  min-height: 40px;
  transition: all var(--t-fast);
  border: 1.5px solid ${({ $selected }) => ($selected ? "var(--c-accent)" : "var(--c-border)")};
  background: ${({ $selected }) => ($selected ? "var(--c-tint)" : "var(--c-surface)")};
  color: ${({ $selected }) => ($selected ? "var(--c-accent)" : "#2e1f0f")};

  &:hover:not(:disabled) { border-color: var(--c-accent); }
  &:disabled { opacity: 0.35; cursor: not-allowed; text-decoration: line-through; }
  &:focus-visible { outline: 2px solid var(--c-accent); outline-offset: 2px; }
`;
TimeSlot.displayName = "TimeSlot";

export const TimezoneNote = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: var(--sp-4);
  padding: 10px 14px;
  background: var(--c-tint);
  border-radius: var(--r-sm);
  font-size: 0.76rem;
  color: var(--c-accent);
  font-weight: 600;

  svg { width: 14px; height: 14px; flex-shrink: 0; }
`;
TimezoneNote.displayName = "TimezoneNote";

// ═══════════════════════════════════════════════════════════════════════════════
// DETAILS FORM
// ═══════════════════════════════════════════════════════════════════════════════

export const CardSubtitle = styled.p`
  font-size: 0.8rem;
  color: var(--c-text-muted, #6b6b63);
  margin: -6px 0 var(--sp-4);
`;
CardSubtitle.displayName = "CardSubtitle";

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--sp-4);

  @media (min-width: ${DESKTOP}) { grid-template-columns: repeat(3, 1fr); }
`;
FormGrid.displayName = "FormGrid";

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
Field.displayName = "Field";

export const Label = styled.label`
  font-size: 0.8rem;
  font-weight: 700;
  color: #2e1f0f;

  em { color: #c0392b; font-style: normal; margin-left: 2px; }
`;
Label.displayName = "Label";

export const InputWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid ${({ $error }) => ($error ? "#c0392b" : "var(--c-border)")};
  border-radius: var(--r-sm);
  background: var(--c-surface);
  padding: 0 12px;
  transition: border-color var(--t-fast);

  &:focus-within { border-color: var(--c-accent); }

  svg { flex-shrink: 0; width: 16px; height: 16px; color: var(--c-text-muted, #a3a396); }
`;
InputWrap.displayName = "InputWrap";

export const CountryCode = styled.select`
  border: none;
  background: none;
  font-size: 0.85rem;
  font-weight: 600;
  color: #2e1f0f;
  padding: 12px 2px;
  cursor: pointer;

  &:focus-visible { outline: none; }
`;
CountryCode.displayName = "CountryCode";

export const Input = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  background: none;
  padding: 12px 0;
  font-size: 0.88rem;
  color: #2e1f0f;

  &::placeholder { color: var(--c-text-muted, #a3a396); }
  &:focus-visible { outline: none; }
`;
Input.displayName = "Input";

export const ErrorText = styled.span`
  font-size: 0.72rem;
  color: #c0392b;
  font-weight: 600;
`;
ErrorText.displayName = "ErrorText";

export const PrivacyNote = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: var(--sp-4);
  font-size: 0.76rem;
  color: var(--c-text-muted, #6b6b63);
  line-height: 1.5;

  svg { flex-shrink: 0; width: 14px; height: 14px; margin-top: 2px; color: var(--c-text-muted, #a3a396); }
`;
PrivacyNote.displayName = "PrivacyNote";

export const SubmitRow = styled.div`
  text-align: center;
  margin-top: var(--sp-5);
`;
SubmitRow.displayName = "SubmitRow";

export const SubmitBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  background: var(--c-accent);
  color: #fff;
  border: none;
  padding: 15px 26px;
  font-size: 0.95rem;
  font-weight: 700;
  border-radius: var(--r-full);
  cursor: pointer;
  transition: transform var(--t-fast), opacity var(--t-fast), filter var(--t-fast);
  box-shadow: var(--s-sm);

  svg { width: 16px; height: 16px; }

  &:hover:not(:disabled) { transform: translateY(-1px); filter: brightness(1.06); }
  &:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
  &:focus-visible { outline: 3px solid var(--c-accent); outline-offset: 2px; }

  @media (min-width: ${DESKTOP}) { width: auto; padding: 16px 40px; }
`;
SubmitBtn.displayName = "SubmitBtn";

export const SubmitHint = styled.p`
  font-size: 0.76rem;
  color: var(--c-text-muted, #6b6b63);
  margin: var(--sp-2) 0 0;
`;
SubmitHint.displayName = "SubmitHint";

export const FormStatus = styled.div`
  margin-top: var(--sp-4);
  padding: 12px 14px;
  border-radius: var(--r-sm);
  font-size: 0.85rem;
  font-weight: 600;
  text-align: left;
  background: ${({ $variant }) => ($variant === "error" ? "#FDEAEA" : "var(--c-tint)")};
  color: ${({ $variant }) => ($variant === "error" ? "#B91C1C" : "var(--c-accent)")};
`;
FormStatus.displayName = "FormStatus";

// ═══════════════════════════════════════════════════════════════════════════════
// SELECTION / SESSION ASIDE
// ═══════════════════════════════════════════════════════════════════════════════

export const AsideCard = styled.div`
  background: var(--c-surface);
  border: 1px solid var(--c-border);
  border-radius: var(--r-lg);
  box-shadow: var(--s-sm);
  padding: var(--sp-5) var(--sp-4);
  text-align: center;
  ${gridArea("aside")}
`;
AsideCard.displayName = "AsideCard";

export const AsideHeading = styled.div`
  display: none;
  align-items: center;
  gap: 8px;
  text-align: left;
  font-size: 0.95rem;
  font-weight: 700;
  color: #2e1f0f;
  margin-bottom: var(--sp-4);

  svg { width: 18px; height: 18px; color: var(--c-accent); }

  @media (min-width: ${DESKTOP}) { display: flex; }
`;
AsideHeading.displayName = "AsideHeading";

export const AsideRows = styled.div`
  display: none;
  flex-direction: column;
  gap: var(--sp-4);
  text-align: left;
  margin-bottom: var(--sp-5);
  padding-bottom: var(--sp-5);
  border-bottom: 1px solid var(--c-border);

  @media (min-width: ${DESKTOP}) { display: flex; }
`;
AsideRows.displayName = "AsideRows";

export const AsideRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;

  svg { width: 18px; height: 18px; color: var(--c-accent); margin-top: 2px; flex-shrink: 0; }
`;
AsideRow.displayName = "AsideRow";

export const AsideRowLabel = styled.div`
  font-size: 0.75rem;
  color: var(--c-text-muted, #6b6b63);
  margin-bottom: 2px;
`;
AsideRowLabel.displayName = "AsideRowLabel";

export const AsideRowValue = styled.div`
  font-size: 0.9rem;
  font-weight: 700;
  color: #2e1f0f;
`;
AsideRowValue.displayName = "AsideRowValue";

export const SessionIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: var(--c-tint);
  color: var(--c-accent);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto var(--sp-3);

  svg { width: 26px; height: 26px; }
`;
SessionIcon.displayName = "SessionIcon";

export const SessionTitle = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  color: var(--c-accent);
  margin: 0 0 6px;
`;
SessionTitle.displayName = "SessionTitle";

export const SessionDesc = styled.p`
  font-size: 0.82rem;
  color: var(--c-text-muted, #6b6b63);
  margin: 0;
  line-height: 1.5;
`;
SessionDesc.displayName = "SessionDesc";

// ─── Mobile step caption ────────────────────────────────────────────────────────

export const StepCaption = styled.div`
  text-align: center;
  margin-top: var(--sp-5);

  strong {
    display: block;
    font-size: 0.7rem;
    font-weight: 800;
    letter-spacing: 0.14em;
    color: var(--c-text-muted, #a3a396);
    margin-bottom: 4px;
  }
  span {
    font-size: 0.88rem;
    font-weight: 700;
    color: #2e1f0f;
  }

  @media (min-width: ${DESKTOP}) { display: none; }
`;
StepCaption.displayName = "StepCaption";

// ═══════════════════════════════════════════════════════════════════════════════
// FEATURE STRIP
// ═══════════════════════════════════════════════════════════════════════════════

export const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--sp-4);
  margin-top: var(--sp-8);

  @media (min-width: ${DESKTOP}) { grid-template-columns: repeat(4, 1fr); }
`;
FeatureGrid.displayName = "FeatureGrid";

export const FeatureItem = styled.div`
  text-align: center;
  ${motion(css`animation: ${kFadeUp} 0.5s ease both;`)}
  animation-delay: ${({ $index = 0 }) => $index * 70}ms;
`;
FeatureItem.displayName = "FeatureItem";

export const FeatureIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: var(--c-tint);
  color: var(--c-accent);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--sp-3);

  svg { width: 22px; height: 22px; }
`;
FeatureIcon.displayName = "FeatureIcon";

export const FeatureTitle = styled.h4`
  font-size: 0.88rem;
  font-weight: 700;
  color: var(--c-accent);
  margin: 0 0 4px;
`;
FeatureTitle.displayName = "FeatureTitle";

export const FeatureDesc = styled.p`
  font-size: 0.78rem;
  color: var(--c-text-muted, #6b6b63);
  margin: 0;
  line-height: 1.4;
`;
FeatureDesc.displayName = "FeatureDesc";

// ─── Bottom note bar ─────────────────────────────────────────────────────────────

export const NoteBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--c-tint);
  border-radius: var(--r-sm);
  padding: var(--sp-4);
  margin-top: var(--sp-8);
  font-size: 0.82rem;
  color: var(--c-accent);

  svg { width: 16px; height: 16px; flex-shrink: 0; }
  strong { font-weight: 800; }
`;
NoteBar.displayName = "NoteBar";